import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

const TimeInput = ({
  value = '',
  onChange,
  placeholder = 'HH:MM',
  className = '',
  disabled = false,
  required = false,
  name = 'time',
}) => {
  const [displayValue, setDisplayValue] = useState('');
  const inputRef = useRef(null);
  // Formata o valor para HH:MM
  const formatTime = (input) => {
    // Remove tudo que não é número
    let numbers = input.replace(/\D/g, '');

    // Limita a 4 dígitos
    if (numbers.length > 4) {
      numbers = numbers.substring(0, 4);
    }

    // Adiciona os dois pontos após os primeiros 2 dígitos
    if (numbers.length > 2) {
      return `${numbers.substring(0, 2)}:${numbers.substring(2)}`;
    }

    return numbers;
  };

  // Valida se a hora é válida
  const isValidTime = (time) => {
    if (!time) return true;

    const [hours, minutes] = time.split(':');
    const hoursNum = parseInt(hours, 10);
    const minutesNum = parseInt(minutes, 10);

    return (
      hoursNum >= 0 && hoursNum <= 23 &&
      minutesNum >= 0 && minutesNum <= 59
    );
  };

  // Atualiza o valor exibido quando o valor prop muda
  useEffect(() => {
    if (value) {
      setDisplayValue(formatTime(value.replace(':', '')));
    } else {
      setDisplayValue('');
    }
  }, [value]);

  const handleChange = (e) => {
    const rawValue = e.target.value;
    const formattedValue = formatTime(rawValue);

    setDisplayValue(formattedValue);

    // Chama onChange apenas se o tempo for válido ou campo vazio
    if (isValidTime(formattedValue) || formattedValue === '') {
      onChange({
        target: {
          name,
          value: formattedValue,
        },
      });
    }
  };

  const handleBlur = () => {
    // Completa com zeros se necessário
    if (displayValue && displayValue.length === 1) {
      const formattedValue = `0${displayValue}:00`;
      setDisplayValue(formattedValue);
      onChange({
        target: {
          name,
          value: formattedValue,
        },
      });
    } else if (displayValue && displayValue.length === 2) {
      const formattedValue = `${displayValue}:00`;
      setDisplayValue(formattedValue);
      onChange({
        target: {
          name,
          value: formattedValue,
        },
      });
    } else if (displayValue && displayValue.length === 4) {
      const formattedValue = `${displayValue.substring(0, 2)}:${displayValue.substring(2)}`;
      setDisplayValue(formattedValue);
      onChange({
        target: {
          name,
          value: formattedValue,
        },
      });
    } else if (displayValue === '') {
      const formattedValue = `00:00`;
      setDisplayValue(formattedValue);
      onChange({
        target: {
          name,
          value: formattedValue,
        },
      });
    }
  };

  const handleKeyDown = (e) => {
    // Permite navegação com teclado
    if (['ArrowLeft', 'ArrowRight', 'Backspace', 'Delete', 'Tab'].includes(e.key)) {
      return;
    }

    // Permite apenas números
    if (!/\d/.test(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <input
      type="text"
      ref={inputRef}
      name={name}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      className={`time-input ${className}`}
      disabled={disabled}
      required={required}
      maxLength={5}
      style={{
        width: '50%',
        height: '50%',
        padding: '12px 16px',
        border: '1px solid #d1d5db',
        borderRadius: '8px',
        fontSize: '16px',
        outline: 'none',
        transition: 'all 0.2s ease',
        boxSizing: 'border-box',
        backgroundColor: disabled ? '#f3f4f6' : '#ffffff',
        color: disabled ? '#9ca3af' : '#111827',
        ':hover': {
          borderColor: '#9ca3af',
        },
        ':focus': {
          borderColor: '#3b82f6',
          boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.2)',
        },
      }}
    />
  );
};

TimeInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  name: PropTypes.string,
};

export default TimeInput;